import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { FONT_MAIN, STATUS_STYLE, glassCard, inputStyle } from "./MobileFamiliares";
import { ArrowLeft, CalendarDays, ChevronLeft, ChevronRight, Loader2, Plus, Search, Users, X } from "lucide-react";

const PAGE_SIZE = 8;

function dataCurta(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit" });
}

function periodoPadrao() {
  const fim = new Date();
  const ini = new Date();
  ini.setDate(ini.getDate() - 30);
  const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return { data_inicial: fmt(ini), data_final: fmt(fim) };
}

function PeriodoModal({ periodo, setPeriodo, onClose, onConfirm, erro }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-6" data-testid="appmobile-visitantes-periodo-modal">
      <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.72)" }} />
      <div className="relative w-full max-w-sm rounded-[14px] overflow-hidden p-5" style={glassCard}>
        <button type="button" data-testid="appmobile-visitantes-periodo-fechar" aria-label="Fechar" onClick={onClose}
          className="absolute right-3 top-3 w-9 h-9 flex items-center justify-center rounded-xl active:bg-white/10"
          style={{ color: "#8d90a0" }}>
          <X className="h-4 w-4" />
        </button>
        <h2 className="text-base font-bold text-center" style={{ color: "#e1e2ed" }}
          data-testid="appmobile-visitantes-periodo-titulo">Período</h2>
        <p className="text-[11px] text-center mt-1 mb-4" style={{ color: "#8d90a0" }}
          data-testid="appmobile-visitantes-periodo-info">Últimos 30 dias até hoje já preenchidos.</p>
        <div className="flex flex-col gap-3">
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider mb-1" style={{ color: "#c3c6d7" }}>Data inicial</label>
            <input type="date" data-testid="appmobile-visitantes-data-inicial" value={periodo.data_inicial}
              onChange={(e) => setPeriodo({ ...periodo, data_inicial: e.target.value })}
              className="w-full rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#b4c5ff]" style={inputStyle} />
          </div>
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider mb-1" style={{ color: "#c3c6d7" }}>Data final</label>
            <input type="date" data-testid="appmobile-visitantes-data-final" value={periodo.data_final}
              onChange={(e) => setPeriodo({ ...periodo, data_final: e.target.value })}
              className="w-full rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#b4c5ff]" style={inputStyle} />
          </div>
          {erro && <p className="text-xs" style={{ color: "#ffb4ab" }} data-testid="appmobile-visitantes-periodo-erro">{erro}</p>}
        </div>
        <div className="flex gap-3 mt-5">
          <button type="button" data-testid="appmobile-visitantes-periodo-voltar" onClick={onClose}
            className="flex-1 py-3 rounded-xl text-sm font-medium"
            style={{ color: "#8d90a0", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)" }}>
            Voltar
          </button>
          <button type="button" data-testid="appmobile-visitantes-periodo-confirmar" onClick={onConfirm}
            className="flex-1 py-3 rounded-xl text-sm font-semibold text-white" style={{ background: "#2563EB" }}>
            Confirmar
          </button>
        </div>
      </div>
    </div>
  );
}

function VisitanteModal({ v, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-5" data-testid="appmobile-visitante-modal">
      <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.72)" }} />
      <div className="relative w-full max-w-sm rounded-[14px] overflow-hidden p-5" style={glassCard}>
        <button type="button" data-testid="appmobile-visitante-fechar" aria-label="Fechar" onClick={onClose}
          className="absolute right-3 top-3 w-9 h-9 flex items-center justify-center rounded-xl active:bg-white/10"
          style={{ color: "#8d90a0" }}>
          <X className="h-4 w-4" />
        </button>
        <div className="pr-8" data-testid="appmobile-visitante-resumo">
          <p className="text-xs font-semibold" style={{ color: "#e1e2ed" }} data-testid="appmobile-visitante-linha1">
            {dataCurta(v.data_cadastro)} · {v.motivo || "—"} · {v.empresa || "—"}
          </p>
          <p className="text-xs mt-2" style={{ color: "#c3c6d7" }} data-testid="appmobile-visitante-linha2">
            {v.nome || "—"} · Autorizador: {v.autorizador || "—"} · Pessoa visitada: {v.pessoa_visitada || "—"}
          </p>
        </div>
        <div className="mt-4 rounded-xl p-3" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)" }}
          data-testid="appmobile-visitante-veiculo">
          <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: "#b4c5ff" }}>Dados do Veículo</p>
          <p className="text-xs" style={{ color: "#e1e2ed" }} data-testid="appmobile-visitante-veiculo-dados">
            {v.placa || "—"} · {v.cor || "—"} · {v.modelo || "—"}
          </p>
        </div>
        <div className="mt-3 rounded-xl p-3" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)" }}
          data-testid="appmobile-visitante-validade">
          <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: "#b4c5ff" }}>Validade</p>
          <p className="text-xs" style={{ color: "#e1e2ed" }} data-testid="appmobile-visitante-validade-dados">
            {dataCurta(v.validade_inicial)} · {dataCurta(v.validade_final)}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function MobileVisitantes({ origem, eql, cpf, onClose }) {
  const [dados, setDados] = useState(null);
  const [falha, setFalha] = useState(false);
  const [skip, setSkip] = useState(0);
  const [busca, setBusca] = useState("");
  const [buscaAplicada, setBuscaAplicada] = useState("");
  const [buscaOpen, setBuscaOpen] = useState(false);
  const [listarTodos, setListarTodos] = useState(false);
  const [periodo, setPeriodo] = useState(periodoPadrao);
  const [periodoAplicado, setPeriodoAplicado] = useState(null);
  const [periodoOpen, setPeriodoOpen] = useState(false);
  const [periodoErro, setPeriodoErro] = useState("");
  const [selecionado, setSelecionado] = useState(null);

  const qs = useMemo(() => `origem=${encodeURIComponent(origem)}&eql=${encodeURIComponent(eql)}&cpf=${cpf}`, [origem, eql, cpf]);
  const carregar = useCallback(async () => {
    try {
      const params = new URLSearchParams(qs);
      params.set("skip", String(skip));
      params.set("limit", String(PAGE_SIZE));
      params.set("listartodos", listarTodos ? "true" : "false");
      if (buscaAplicada) params.set("busca", buscaAplicada);
      if (listarTodos && periodoAplicado?.data_inicial) params.set("data_inicial", periodoAplicado.data_inicial);
      if (listarTodos && periodoAplicado?.data_final) params.set("data_final", periodoAplicado.data_final);
      const r = await fetch(`${API}/public/visitantes?${params.toString()}`);
      const d = await r.json();
      if (d.ok) { setDados(d); setFalha(false); }
      else { setFalha(true); toast.error("Não foi possível carregar os visitantes."); }
    } catch { setFalha(true); toast.error("Falha de comunicação. Tente novamente."); }
  }, [qs, skip, buscaAplicada, listarTodos, periodoAplicado]);

  useEffect(() => { carregar(); }, [carregar]);

  const aplicarBusca = () => {
    setBuscaAplicada(busca.trim());
    setSkip(0);
  };

  const abrirPeriodo = () => {
    setPeriodo(periodoAplicado || periodoPadrao());
    setPeriodoErro("");
    setPeriodoOpen(true);
  };

  const confirmarPeriodo = () => {
    if (!periodo.data_inicial || !periodo.data_final) {
      setPeriodoErro("Informe data inicial e final.");
      return;
    }
    if (periodo.data_final < periodo.data_inicial) {
      setPeriodoErro("A data final não pode ser anterior à inicial.");
      return;
    }
    setListarTodos(true);
    setPeriodoAplicado(periodo);
    setSkip(0);
    setPeriodoOpen(false);
  };

  const voltarAtivos = () => {
    setListarTodos(false);
    setPeriodoAplicado(null);
    setSkip(0);
  };

  const itens = dados?.visitantes || [];
  const total = dados?.total || 0;
  const pagina = Math.floor(skip / PAGE_SIZE) + 1;
  const totalPaginas = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="fixed inset-0 z-40 flex flex-col" style={{ background: "#11131b", fontFamily: FONT_MAIN }}
      data-testid="appmobile-visitantes-overlay">
      <header className="flex items-center gap-3 px-4 py-3.5"
        style={{ borderBottom: "1px solid rgba(255,255,255,0.06)", paddingTop: "max(14px, env(safe-area-inset-top))" }}>
        <button type="button" data-testid="appmobile-visitantes-voltar" aria-label="Voltar" onClick={onClose}
          className="w-10 h-10 flex items-center justify-center rounded-xl active:bg-white/10"
          style={{ color: "#c3c6d7", touchAction: "manipulation" }}>
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="flex-1 text-base font-bold" style={{ color: "#b4c5ff" }} data-testid="appmobile-visitantes-titulo">Visitantes</h1>
        <button type="button" data-testid="appmobile-visitantes-incluir" aria-label="Incluir visitante"
          className="inline-flex items-center gap-1 rounded-xl px-3 h-9 text-xs font-semibold"
          style={{ background: "rgba(37,99,235,0.2)", color: "#8ab4ff", border: "1px solid rgba(138,180,255,0.3)", touchAction: "manipulation" }}>
          <Plus className="h-4 w-4" /> Incluir
        </button>
        <button type="button" data-testid="appmobile-visitantes-busca-btn" aria-label="Buscar"
          onClick={() => setBuscaOpen(!buscaOpen)}
          className="w-10 h-10 flex items-center justify-center rounded-xl active:bg-white/10"
          style={{ color: buscaOpen ? "#8ab4ff" : "#c3c6d7" }}>
          <Search className="h-5 w-5" />
        </button>
      </header>

      <main className="flex-1 overflow-y-auto px-4 py-4 pb-28">
        <div className="flex gap-2 mb-3" data-testid="appmobile-visitantes-acoes">
          <button type="button" data-testid="appmobile-visitantes-listar-todos" onClick={abrirPeriodo}
            className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl py-2.5 text-xs font-semibold active:scale-[0.98] transition-transform"
            style={{ background: listarTodos ? "rgba(37,99,235,0.2)" : "rgba(255,255,255,0.06)", color: "#b4c5ff", border: "1px solid rgba(138,180,255,0.25)" }}>
            <Users className="h-4 w-4" /> Listar todos
          </button>
          <button type="button" data-testid="appmobile-visitantes-periodo" onClick={abrirPeriodo}
            className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl py-2.5 text-xs font-semibold active:scale-[0.98] transition-transform"
            style={{ background: "rgba(255,255,255,0.06)", color: "#c3c6d7", border: "1px solid rgba(255,255,255,0.1)" }}>
            <CalendarDays className="h-4 w-4" /> Período
          </button>
        </div>

        {buscaOpen && (
          <form className="mb-3 flex gap-2" data-testid="appmobile-visitantes-busca-form"
            onSubmit={(e) => { e.preventDefault(); aplicarBusca(); }}>
            <input data-testid="appmobile-visitantes-busca-input" value={busca} onChange={(e) => setBusca(e.target.value)}
              placeholder="Nome ou CPF" className="flex-1 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#b4c5ff]"
              style={inputStyle} />
            <button type="submit" data-testid="appmobile-visitantes-buscar" aria-label="Buscar"
              className="w-11 rounded-xl flex items-center justify-center text-white" style={{ background: "#2563EB" }}>
              <Search className="h-4 w-4" />
            </button>
          </form>
        )}

        {listarTodos && (
          <div className="mb-3 flex items-center justify-between rounded-xl px-3 py-2" style={glassCard}
            data-testid="appmobile-visitantes-periodo-ativo">
            <span className="text-[11px]" style={{ color: "#c3c6d7" }}>
              Todos · {dataCurta(periodoAplicado?.data_inicial)} até {dataCurta(periodoAplicado?.data_final)}
            </span>
            <button type="button" data-testid="appmobile-visitantes-voltar-ativos" onClick={voltarAtivos}
              className="text-[11px] font-semibold" style={{ color: "#8ab4ff" }}>
              Ativos
            </button>
          </div>
        )}

        {!dados ? (
          falha ? (
            <div className="flex flex-col items-center gap-3 py-16" data-testid="appmobile-visitantes-erro">
              <p className="text-center text-xs" style={{ color: "#8d90a0" }}>Não foi possível carregar os visitantes.</p>
              <button type="button" data-testid="appmobile-visitantes-tentar-novamente" onClick={carregar}
                className="rounded-xl px-4 py-2.5 text-xs font-semibold text-white" style={{ background: "#2563EB" }}>
                Tentar novamente
              </button>
            </div>
          ) : (
            <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin" style={{ color: "#8d90a0" }} /></div>
          )
        ) : itens.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-16" data-testid="appmobile-visitantes-vazio">
            <Users className="h-8 w-8" style={{ color: "#8d90a0" }} />
            <p className="text-center text-xs" style={{ color: "#8d90a0" }}>
              {listarTodos ? "Nenhum visitante no período." : "Nenhum visitante ativo."}
            </p>
          </div>
        ) : (
          <>
            <div className="rounded-xl overflow-hidden" style={glassCard} data-testid="appmobile-visitantes-lista">
              <div className="grid grid-cols-[54px_minmax(0,1fr)_66px_64px_48px] gap-2 px-3 py-2.5"
                style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                <span className="text-[9px] font-bold uppercase tracking-wider" style={{ color: "#8d90a0" }}>Data</span>
                <span className="text-[9px] font-bold uppercase tracking-wider" style={{ color: "#8d90a0" }}>Nome</span>
                <span className="text-[9px] font-bold uppercase tracking-wider" style={{ color: "#8d90a0" }}>Motivo</span>
                <span className="text-[9px] font-bold uppercase tracking-wider" style={{ color: "#8d90a0" }}>Autorizador</span>
                <span className="text-[9px] font-bold uppercase tracking-wider text-center" style={{ color: "#8d90a0" }}>Ativo</span>
              </div>
              {itens.map((v) => {
                const st = v.ativo ? STATUS_STYLE.Ativo : STATUS_STYLE.Inativo;
                return (
                  <button key={v.id} type="button" data-testid={`appmobile-visitante-item-${v.id}`} onClick={() => setSelecionado(v)}
                    className="w-full grid grid-cols-[54px_minmax(0,1fr)_66px_64px_48px] gap-2 items-center px-3 py-3 text-left active:bg-white/5"
                    style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                    <span className="text-[10px]" style={{ color: "#c3c6d7" }} data-testid={`appmobile-visitante-data-${v.id}`}>{dataCurta(v.data_cadastro)}</span>
                    <span className="min-w-0 truncate text-[11px] font-semibold" style={{ color: "#e1e2ed" }} data-testid={`appmobile-visitante-nome-${v.id}`}>{v.nome || "—"}</span>
                    <span className="min-w-0 truncate text-[10px]" style={{ color: "#c3c6d7" }} data-testid={`appmobile-visitante-motivo-${v.id}`}>{v.motivo || "—"}</span>
                    <span className="min-w-0 text-[9px]" style={{ color: "#c3c6d7" }} data-testid={`appmobile-visitante-autorizador-${v.id}`}>
                      <span className="block truncate">{v.autorizador || "—"}</span>
                      <span className="block truncate" style={{ color: "#8d90a0" }}>Visitada: {v.pessoa_visitada || "—"}</span>
                    </span>
                    <span className="text-[9px] font-bold text-center rounded-full px-1 py-1"
                      style={{ background: st.bg, color: st.color, border: `1px solid ${st.border}` }}
                      data-testid={`appmobile-visitante-ativo-${v.id}`}>{v.ativo ? "Ativo" : "Inativo"}</span>
                  </button>
                );
              })}
            </div>
            <div className="mt-3 flex items-center justify-between" data-testid="appmobile-visitantes-paginacao">
              <button type="button" data-testid="appmobile-visitantes-anterior" onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
                disabled={skip === 0}
                className="inline-flex items-center gap-1 rounded-xl px-3 py-2 text-xs font-semibold disabled:opacity-35"
                style={{ background: "rgba(255,255,255,0.06)", color: "#c3c6d7", border: "1px solid rgba(255,255,255,0.1)" }}>
                <ChevronLeft className="h-4 w-4" /> Anterior
              </button>
              <span className="text-[11px]" style={{ color: "#8d90a0" }} data-testid="appmobile-visitantes-pagina">
                {pagina}/{totalPaginas} · {total}
              </span>
              <button type="button" data-testid="appmobile-visitantes-proximo" onClick={() => setSkip(skip + PAGE_SIZE)}
                disabled={skip + (dados?.count || 0) >= total}
                className="inline-flex items-center gap-1 rounded-xl px-3 py-2 text-xs font-semibold disabled:opacity-35"
                style={{ background: "rgba(255,255,255,0.06)", color: "#c3c6d7", border: "1px solid rgba(255,255,255,0.1)" }}>
                Próximo <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </>
        )}
      </main>

      {periodoOpen && (
        <PeriodoModal periodo={periodo} setPeriodo={setPeriodo} erro={periodoErro}
          onClose={() => setPeriodoOpen(false)} onConfirm={confirmarPeriodo} />
      )}
      {selecionado && <VisitanteModal v={selecionado} onClose={() => setSelecionado(null)} />}
    </div>
  );
}
